/**
 * 认证相关工具函数
 * 提供更高级别的登录状态管理和控制
 */
import {
  wxSilentLogin,
  isLoggedIn,
  logout,
  getLocalUserInfo,
  saveUserInfo,
} from '../modules/login';

/**
 * 清除所有登录相关数据
 */
export function clearLoginData(): void {
  uni.removeStorageSync('token');
  uni.removeStorageSync('user');
  uni.removeStorageSync('tokenTimestamp');
  uni.removeStorageSync('tokenExpiresIn');
  console.log('登录数据已清除');
}

/**
 * 获取当前存储的Token
 * @returns string Token字符串，如果不存在则返回空字符串
 */
export function getToken(): string {
  return uni.getStorageSync('token') || '';
}

/**
 * 获取当前用户信息
 * @returns any 用户信息对象，如果不存在则返回null
 */
export function getCurrentUser(): any {
  const userStr = uni.getStorageSync('user');
  return userStr ? JSON.parse(userStr) : null;
}

/**
 * 保存登录数据到本地存储
 * @param loginData 登录接口返回的数据
 * @example
 * // 示例用法：处理登录接口返回的数据
 * const loginResponse = {
 *   "code": 200,
 *   "message": "登录成功",
 *   "data": {
 *     "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
 *     "user": {
 *       "id": "f6c99caa-4f6e-47b0-9a8a-6c61b1aa1f90",
 *       "openid": "mp_38b6a9359fff4f2d9b2e622fa54f3833",
 *       "nickname": "小程序用户",
 *       "avatar": ""
 *     }
 *   }
 * };
 *
 * // 调用saveLoginData函数保存登录数据到本地存储
 * if (loginResponse.code === 200 && loginResponse.data) {
 *   saveLoginData(loginResponse.data);
 * }
 */
export function saveLoginData(loginData: {
  token: string;
  user: {
    id: string;
    openid: string;
    nickname: string;
    avatar: string;
  };
}): void {
  // 保存token
  uni.setStorageSync('token', loginData.token);

  // 保存用户信息
  uni.setStorageSync('user', JSON.stringify(loginData.user));
  saveUserInfo(loginData.user);

  // 记录token获取时间（用于日志和调试）
  uni.setStorageSync('tokenTimestamp', Date.now().toString());

  console.log('登录数据已保存到本地存储');
}

/**
 * 确保用户已登录
 * 如果未登录则自动触发静默登录
 * @returns Promise<boolean> 登录是否成功
 */
export async function ensureLogin(): Promise<boolean> {
  // #ifdef H5
  console.log('H5 模式: 跳过登录验证');
  return true;
  // #endif

  // #ifndef H5
  try {
    if (!isLoggedIn()) {
      console.log('用户未登录，触发静默登录');
      const loginResult = await wxSilentLogin();

      if (loginResult.code === 200 && loginResult.data?.token) {
        console.log('静默登录成功');
        return true;
      } else {
        console.error('静默登录失败:', loginResult.message);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('确保登录时发生错误:', error);
    return false;
  }
  // #endif
}

/**
 * 检查权限并执行操作
 * 如果未登录会自动尝试登录
 * @param callback 有权限时执行的回调函数
 * @param fallback 无权限时的回调函数（可选）
 * @returns Promise<any> 回调函数的返回值
 */
export async function withAuth<T>(
  callback: () => Promise<T>,
  fallback?: () => T | Promise<T>
): Promise<T | any> {
  try {
    // 确保已登录
    const isAuthenticated = await ensureLogin();

    if (isAuthenticated) {
      // 已登录则执行回调
      return await callback();
    } else if (fallback) {
      // 未登录但提供了fallback回调，则执行fallback
      return typeof fallback === 'function' ? await fallback() : fallback;
    } else {
      // 未登录且未提供fallback，则抛出异常
      throw new Error('用户未登录，无权限执行此操作');
    }
  } catch (error) {
    console.error('权限检查失败:', error);
    throw error;
  }
}

/**
 * 生成授权请求头
 * 用于需要认证的API请求
 * @returns object 请求头对象
 */
export function getAuthHeaders(): Record<string, string> {
  const token = uni.getStorageSync('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * 更新用户信息
 * 保存到本地存储并返回更新后的信息
 * @param newInfo 新的用户信息
 * @returns object 更新后的用户信息
 */
export function updateUserInfo(newInfo: Record<string, any>): Record<string, any> {
  try {
    // 获取当前用户信息
    const currentInfo = getLocalUserInfo() || {};

    // 合并新旧信息
    const updatedInfo = { ...currentInfo, ...newInfo };

    // 保存更新后的信息
    saveUserInfo(updatedInfo);

    return updatedInfo;
  } catch (error) {
    console.error('更新用户信息时发生错误:', error);
    throw error;
  }
}

export default {
  ensureLogin,
  withAuth,
  getAuthHeaders,
  updateUserInfo,
  saveLoginData,
  getToken,
  getCurrentUser,
  clearLoginData,
};

/**
 * 使用示例
 *
 * 1. 保存登录数据
 * ```typescript
 * import { saveLoginData } from '@/api/utils/auth';
 *
 * // 处理登录接口返回的数据
 * const loginResponse = {
 *   "code": 200,
 *   "message": "登录成功",
 *   "data": {
 *     "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
 *     "refresh": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
 *     "user": {
 *       "id": "f6c99caa-4f6e-47b0-9a8a-6c61b1aa1f90",
 *       "openid": "mp_38b6a9359fff4f2d9b2e622fa54f3833",
 *       "nickname": "小程序用户",
 *       "avatar": ""
 *     }
 *   }
 * };
 *
 * if (loginResponse.code === 200 && loginResponse.data) {
 *   saveLoginData(loginResponse.data);
 * }
 * ```
 *
 * 2. 获取Token和用户信息
 * ```typescript
 * import { getToken, getCurrentUser } from '@/api/utils/auth';
 *
 * const token = getToken();
 * const user = getCurrentUser();
 * console.log('当前用户:', user?.nickname);
 * ```
 *
 * 3. 使用withAuth进行权限控制
 * ```typescript
 * import { withAuth } from '@/api/utils/auth';
 *
 * async function getUserData() {
 *   return await withAuth(
 *     async () => {
 *       // 已登录，执行需要权限的操作
 *       const response = await http.get('/api/user/profile');
 *       return response.data;
 *     },
 *     async () => {
 *       // 未登录，返回默认值或跳转登录页
 *       uni.navigateTo({ url: '/pages/login/index' });
 *       return null;
 *     }
 *   );
 * }
 * ```
 *
 * 4. 登出时清除数据
 * ```typescript
 * import { clearLoginData } from '@/api/utils/auth';
 *
 * function handleLogout() {
 *   clearLoginData();
 *   // 跳转到登录页
 *   uni.reLaunch({ url: '/pages/login/index' });
 * }
 * ```
 */
